import { Box, Flex, Image, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';

import nakheelBaghdad from 'assets/partner-nakheel-baghdad.webp';
import gharraf from 'assets/partner-gharraf.webp';
import beboon from 'assets/partner-beboon.webp';
import wisteria from 'assets/partner-wisteria.webp';
import zawraa from 'assets/partner-zawraa.webp';
import ziya from 'assets/partner-ziya.webp';
import soulEscape from 'assets/partner-soul-escape.webp';
import film from 'assets/partner-film.webp';
import bEvents from 'assets/partner-b-events.webp';
import collageClub from 'assets/partner-collage-club.webp';
import fame from 'assets/partner-fame.webp';
import aMusic from 'assets/partner-a-music.webp';
import hy from 'assets/partner-hy.webp';
import baghdad from 'assets/partner-baghdad.webp';
import bmt from 'assets/partner-bmt.webp';
import albahr from 'assets/partner-al-bahr-al-abyad.webp';
import ahsan from 'assets/partner-ahsan.webp';
import peaceOrchestra from 'assets/partner-peace-orchestra.webp';
import raEvents from 'assets/partner-ra-events.webp';

import { SectionHeader } from '../SectionHeader/SectionHeader';

interface Partner {
  id: string;
  src: string;
  /** Brand name, used as the logo's alt text. */
  name: string;
}

const PARTNERS: Partner[] = [
  { id: 'nakheel', src: nakheelBaghdad, name: 'نخيل بغداد' },
  { id: 'gharraf', src: gharraf, name: 'نادي الغراف الرياضي' },
  { id: 'beboon', src: beboon, name: 'Beboon' },
  { id: 'wisteria', src: wisteria, name: 'Wisteria Music' },
  { id: 'zawraa', src: zawraa, name: 'نادي الزوراء الرياضي' },
  { id: 'ziya', src: ziya, name: 'Ziya Group' },
  { id: 'soulEscape', src: soulEscape, name: 'Soul Escape' },
  { id: 'film', src: film, name: 'سينما' },
  { id: 'bEvents', src: bEvents, name: 'B Events' },
  { id: 'collageClub', src: collageClub, name: 'Collage Club' },
  { id: 'fame', src: fame, name: 'FAME Entertainment' },
  { id: 'amusic', src: aMusic, name: 'A Music' },
  { id: 'hy', src: hy, name: 'HY' },
  { id: 'baghdad', src: baghdad, name: 'بغداد' },
  { id: 'bmt', src: bmt, name: 'BMT' },
  { id: 'albahr', src: albahr, name: 'البحر الأبيض للإنتاج والتوزيع الفني' },
  { id: 'ahsan', src: ahsan, name: 'احسن سوية' },
  { id: 'peaceOrchestra', src: peaceOrchestra, name: 'Peace Orchestra of Iraqi Oud' },
  { id: 'raEvents', src: raEvents, name: 'RA Events' },
];

/**
 * About page Partners section (TZ-390): the "جنباً إلى جنب مع أفضل المؤسسات" centered
 * header over a grid of partner/sponsor logo cells. The grid reflows from 4 columns on
 * desktop to 3 / 2 on smaller screens, with a gap between columns and tiles stacked
 * (connected) within each column. Each tile is a white bordered cell, and the logos are
 * desaturated to grey to match the design. RTL section.
 */
export const PartnersSection = () => (
  <Box as='section' dir='rtl' bg='#FAFAF9' aria-labelledby='about-partners-heading'>
    <VStack
      w='100%'
      maxW='container.xl'
      mx='auto'
      px={{ base: '20px', md: '100px' }}
      py={{ base: '48px', md: '80px' }}
      spacing={{ base: '32px', md: '48px' }}
      align='center'
    >
      <SectionHeader
        titleId='about-partners-heading'
        align='center'
        eyebrow={<FormattedMessage id='about.partners.eyebrow' defaultMessage='Our partners' />}
        eyebrowProps={{ fontWeight: '700', fontSize: { base: '15px', md: '16px' } }}
        title={
          <FormattedMessage
            id='about.partners.title'
            defaultMessage='Side by side with the best institutions'
          />
        }
        titleProps={{ fontWeight: '900', fontSize: { base: '28px', md: '40px' } }}
      />

      <Box
        display='grid'
        dir='rtl'
        w='100%'
        maxW='832px'
        gridTemplateColumns={{ base: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }}
        columnGap={{ base: '12px', md: '16px' }}
        rowGap='0'
      >
        {PARTNERS.map((partner) => (
          <Flex
            key={partner.id}
            align='center'
            justify='center'
            h='120px'
            px='24px'
            bg='#FFFFFF'
            // Columns are separated by a gap, so each tile carries a full border. Pull
            // rows up 1px so the shared horizontal divider within a column stays 1px
            // (instead of doubling) while the column edges read as one connected stack.
            border='1px solid'
            borderColor='#E8E5E2'
            mt='-1px'
          >
            <Image
              src={partner.src}
              alt={partner.name}
              // Logos are pre-normalized to a square canvas, so a uniform square box
              // renders them all at the same visual size regardless of aspect ratio.
              boxSize={{ base: '76px', md: '88px' }}
              objectFit='contain'
              loading='lazy'
              // Desaturate to grey to match the design's monochrome logo wall.
              sx={{ filter: 'grayscale(1)' }}
            />
          </Flex>
        ))}
      </Box>
    </VStack>
  </Box>
);
